const DEFAULT_PICKUP_LABEL = 'Terminal';
const NONE_VALUE = 'NONE';

/**
 * Splits composite location strings from search/API into a short pick-up label and street address.
 * Examples:
 * - "In Terminal:Sangsters Intl Airport (MBJ) Montego Bay Jamaica" → label "In Terminal", street rest
 * - "Terminal:In Terminal:Sangsters Intl Airport..." → label "In Terminal" (between 1st and 2nd colon)
 * - No ":" → label "Terminal", street = full string trimmed
 *
 * @param {string|null|undefined} fullAddress
 * @param {{ streetFallback?: string }} [options]
 * @returns {{ locationLabel: string, streetAddress: string }}
 */
export function parseLocationAddressForDisplay(fullAddress, options = {}) {
    const { streetFallback = '' } = options;
    const trimmed = fullAddress != null ? String(fullAddress).trim() : '';

    if (!trimmed) {
        return {
            locationLabel: DEFAULT_PICKUP_LABEL,
            streetAddress: streetFallback,
        };
    }

    if (!trimmed.includes(':')) {
        return {
            locationLabel: DEFAULT_PICKUP_LABEL,
            streetAddress: trimmed,
        };
    }

    const segments = trimmed.split(':');
    const colonCount = segments.length - 1;

    if (colonCount === 1) {
        const locationLabel = (segments[0] || '').trim() || DEFAULT_PICKUP_LABEL;
        const streetAddress = segments.slice(1).join(':').trim() || trimmed;
        return { locationLabel, streetAddress };
    }

    const locationLabel = (segments[1] || '').trim() || DEFAULT_PICKUP_LABEL;
    const streetAddress = segments.slice(2).join(':').trim() || trimmed;
    return { locationLabel, streetAddress };
}

/** @typedef {'airport' | 'phone' | 'city' | 'shuttle' | 'meet' | 'default'} LocationPickerIconKind */

/**
 * Pick an icon category from the raw address / label text (case-insensitive).
 * Order matters: longer phrases (e.g. meet and greet) before loose matches.
 *
 * @param {string|null|undefined} fullAddress
 * @returns {LocationPickerIconKind}
 */
export function getLocationPickerIconKind(fullAddress) {
    const s = fullAddress != null ? String(fullAddress).toLowerCase() : '';
    if (!s) return 'default';
    if (s.includes('meet and greet') || s.includes('meet & greet')) return 'meet';
    if (s.includes('shuttle')) return 'shuttle';
    if (s.includes('terminal')) return 'airport';
    if (/\bcall\b/.test(s) || s.includes('pickup') || s.includes('pick-up') || s.includes('pick up')) {
        return 'phone';
    }
    if (/\bcity\b/.test(s)) return 'city';
    return 'default';
}

/**
 * Parse SEARCH_ADDRESS response value: "Type:Address:AirportIATACode".
 * If Type or AirportIATACode is missing in DB, API returns "NONE".
 *
 * @param {string|null|undefined} fullAddress
 * @returns {{ rawType: string, type: string, address: string, airportIataCode: string }}
 */
export function parseSearchAddressEntry(fullAddress) {
    const raw = fullAddress != null ? String(fullAddress).trim() : '';
    if (!raw) {
        return { rawType: '', type: '', address: '', airportIataCode: '' };
    }

    const segments = raw.split(':');
    if (segments.length < 3) {
        return {
            rawType: '',
            type: '',
            address: raw,
            airportIataCode: '',
        };
    }

    const rawType = (segments[0] || '').trim();
    const rawAirportIataCode = (segments[segments.length - 1] || '').trim();
    const address = segments.slice(1, -1).join(':').trim();

    const type = rawType.toUpperCase() === NONE_VALUE ? '' : rawType;
    const airportIataCode =
        rawAirportIataCode.toUpperCase() === NONE_VALUE ? '' : rawAirportIataCode;

    return {
        rawType,
        type,
        address: address || raw,
        airportIataCode,
    };
}

/**
 * Pick icon by Type token from SEARCH_ADDRESS payload.
 * If Type is NONE/empty, fallback to city icon.
 *
 * @param {string|null|undefined} typeValue
 * @returns {LocationPickerIconKind}
 */
export function getLocationPickerIconKindFromType(typeValue) {
    const type = typeValue != null ? String(typeValue).trim().toLowerCase() : '';
    if (!type || type === 'none') return 'city';
    if (type.includes('meet and greet') || type.includes('meet & greet') || type.includes('meet')) return 'meet';
    if (type.includes('shuttle')) return 'shuttle';
    if (type.includes('terminal') || type.includes('airport')) return 'airport';
    if (type.includes('call') || type.includes('pickup') || type.includes('pick-up') || type.includes('pick up')) {
        return 'phone';
    }
    if (type.includes('city')) return 'city';
    return 'city';
}

/**
 * Split on the first "(...)" pair: line 1 = text before "(" (bold) + full parenthetical;
 * line 2 = remainder after ")", smaller type.
 *
 * @param {string} text
 * @returns {{ type: 'paren', beforeOpen: string, parenBlock: string, afterClose: string } | { type: 'balanced', line1: string, line2: string }}
 */
export function splitAddressForDropdownLines(text) {
    const t = text != null ? String(text).trim() : '';
    if (!t) {
        return { type: 'balanced', line1: '', line2: '' };
    }

    const openIdx = t.indexOf('(');
    const closeIdx = openIdx === -1 ? -1 : t.indexOf(')', openIdx + 1);

    if (openIdx !== -1 && closeIdx !== -1) {
        return {
            type: 'paren',
            beforeOpen: t.slice(0, openIdx),
            parenBlock: t.slice(openIdx, closeIdx + 1),
            afterClose: t.slice(closeIdx + 1).trim(),
        };
    }

    const [line1, line2] = splitIntoTwoBalancedLines(t);
    return { type: 'balanced', line1, line2 };
}

/**
 * Split into two lines of roughly equal character length, preferring a space near the midpoint.
 *
 * @param {string} text
 * @returns {[string, string]}
 */
export function splitIntoTwoBalancedLines(text) {
    const t = text != null ? String(text).trim() : '';
    if (!t) return ['', ''];
    if (t.length <= 2) return [t, ''];

    const mid = Math.floor(t.length / 2);
    let breakAt = -1;

    for (let d = 0; d < t.length; d += 1) {
        const right = mid + d;
        if (right < t.length && t[right] === ' ') {
            breakAt = right;
            break;
        }
        const left = mid - d;
        if (left > 0 && t[left] === ' ') {
            breakAt = left;
            break;
        }
    }

    if (breakAt === -1) {
        const line1 = t.slice(0, mid).trim();
        const line2 = t.slice(mid).trim();
        return line1 ? [line1, line2] : ['', line2];
    }

    const line1 = t.slice(0, breakAt).trim();
    const line2 = t.slice(breakAt + 1).trim();
    if (!line2) return [line1, ''];
    if (!line1) return splitIntoTwoBalancedLines(line2);
    return [line1, line2];
}
